import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import FAQs, { type FAQ } from 'common/www/Faqs';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import LandingFeaturesCTA from 'common/www/LandingFeaturesCTA';
import LandingHowItWorks from 'common/www/LandingHowItWorks';
import LandingPoint, { type LandingPointProps } from 'common/www/LandingPoint';
import LandingUseCaseHelmet from 'common/www/LandingUseCaseHelmet';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Point5 from 'img/landing/ai-features.webp';
import Chelsea from 'img/landing/chelsea.webp';
import LandingHero from 'img/landing/customer-feedback-hero.webp';
import Point4 from 'img/landing/feature-highlight-1-2x.webp';
import Point1 from 'img/landing/feature-request-tracking-1.webp';
import Point2 from 'img/landing/feature-request-tracking-2.webp';
import Point3 from 'img/landing/feature-request-tracking-3.webp';
import Tim from 'img/landing/tim.webp';
import Zeb from 'img/landing/zeb.webp';

import 'css/components/www/_LandingSolutionsPage.scss';

const meta = {
  title: 'Customer Feedback Management Tools',
  description:
    "Canny's customer feedback tools help you capture, analyze, and prioritize your most important feedback and build better products. Get started for free!",
  pageUrl: 'https://canny.io/use-cases/customer-feedback',
};

const points: LandingPointProps[] = [
  {
    image: { src: Point1, alt: 'Feedback collected and organized in a feature voting board' },
    title: 'All your customer feedback in one place',
    subtitle: 'Leave endless spreadsheets and email chains behind',
    loadImageEagerly: true,
    points: [
      'Invite customers to leave feedback and feature requests directly on your board',
      'Automatically pass feedback from customer conversations in your CRM and chat tools',
      "Let your team add feedback and votes on your users' behalf",
      'Prevent duplicate feedback - let users add votes and comments to existing posts',
    ],
  },
  {
    image: { src: Point2, alt: 'Add feedback on behalf of your customers via different sources' },
    title: 'Capture feedback where you connect with your users',
    subtitle: 'Our integrations let you easily collect feedback and send it to Canny',
    points: [
      'Gather feedback while chatting with customers in Intercom and Zendesk',
      'Let sales track feedback on behalf of contacts in Salesforce and HubSpot',
      'Capture feedback anywhere on the web and social media with our Chrome extension',
      'Get feedback on any page of your site with our website feedback tool widget',
    ],
  },
  {
    image: { src: Point3, alt: 'Existing customer profiles including feedback they submitted' },
    title: 'Understand what features your top customers want',
    subtitle: 'Connect your customer data to see who is leaving feedback',
    points: [
      'Create segments (like paying customers) to see what features those users want',
      'Understand which features would help your sales team close more deals',
      'Build your roadmap prioritization formula to surface the most important features',
      'Connect Google Analytics to learn more about who leaves you feedback',
    ],
  },
  {
    image: {
      src: Point4,
      alt: "Instantly reply to several customers at once to show you're listening",
    },
    title: 'Engage your users and improve customer experience',
    subtitle: 'Respond directly to customer feedback and make users a part of your product process',
    points: [
      'Ask probing questions to understand the “why” behind their requests',
      'Let users interact with each other and build a community around your product',
      'Keep everyone updated as you add features to your roadmap and ship them',
      'Build customer loyalty and improve customer retention',
    ],
  },
  {
    image: { src: Point5, alt: 'Let AI help you to respond and summarize' },
    title: 'Dive deeper and save time with powerful AI features',
    subtitle: 'AI powers several key features that help you understand your customers better',
    points: [
      "Let Smart Replies automatically respond to customers' feedback with probing questions",
      'Use comment summaries to instantly recap lengthy feedback threads',
    ],
  },
];

const quotes = [
  {
    header:
      "ClickUp uses Canny's user feedback software to track requests from their growing user base",
    text: "Getting Canny set up couldn't have been easier. We started with a blank canvas and allowed our users to paint the rest.",
    person: <QuotePerson name="Zeb Evans" image={Zeb} title="Founder at ClickUp" />,
  },
  {
    header:
      "Canny's customer feedback tools help Aryeo reduce customer support queries and drive engagement",
    text: 'Canny has reduced the number of inbounds in our support inbox by 20%, which is a big deal.',
    person: (
      <QuotePerson
        name="Chelsea Darby"
        image={Chelsea}
        title={<>Customer Success Lead at&nbsp;Aryeo</>}
      />
    ),
  },
  {
    header: 'Ahrefs uses Canny to simplify feedback collection and understand customer needs ',
    text: 'The learning curve was almost nothing for me because Canny was so simple and intuitive. If I were to create customer feedback software, this is exactly how it would work.',
    person: <QuotePerson name="Tim Suolo" image={Tim} title="CMO at Ahrefs" />,
  },
];

const faqs: FAQ[] = [
  {
    question: 'How does Canny integrate with my site?',
    answer: `<p>Installing Canny on your site is quick and easy. When you sign up you'll get your own portal that you can link to from your website app.</p><p>Canny's user-friendly interface complements your user experience:</p><ul><li><p>Customize your feedback portal with your brand colors and logo</p></li><li><p>Install our feedback widget on any page to give users a quick and easy feedback form</p></li><li><p>Let users share feedback via your Intercom chat</p></li></ul><p>You can also add Canny to any page on your site by <a href="https://help.canny.io/en/articles/1058407-the-canny-widget" target="_blank" rel="noopener">embedding our widget.</a></p>`,
    html: (
      <>
        <p>
          Installing Canny on your site is quick and easy. When you sign up you'll get your own
          portal that you can link to from your website app.
        </p>
        <p>Canny's user-friendly interface complements your user experience:</p>
        <ul className="orderedList">
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Customize your feedback portal with your brand colors and logo
            </p>
          </li>
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Install our feedback widget on any page to give users a quick and easy feedback form
            </p>
          </li>
          <li className="orderedListItem">
            <p className="orderedListItemCopy">Let users share feedback via your Intercom chat</p>
          </li>
        </ul>
        <p>
          You can also add Canny to any page on your site by{' '}
          <a
            href="https://help.canny.io/en/articles/1058407-the-canny-widget"
            target="_blank"
            rel="noopener">
            embedding our widget.
          </a>
        </p>
      </>
    ),
  },
  {
    question: 'Can Canny help me boost my customer satisfaction metrics?',
    answer:
      "<p>Definitely! Listening to customer feedback goes a long way in improving customer sentiment. When clients see that you're working on their feature requests, they trust you more.</p><p>Clients often see improvements in net promoter scores (NPS) and customer effort scores (CES).</p><p>Plus, it can help you collect customer reviews. Some clients see improved G2 ratings with reviewers praising responsiveness to feedback.</p>",
    html: (
      <>
        <p>
          Definitely! Listening to customer feedback goes a long way in improving customer
          sentiment. When clients see that you're working on their feature requests, they trust you
          more.
        </p>
        <p>
          Clients often see improvements in net promoter scores (NPS) and customer effort scores
          (CES).
        </p>
        <p>
          Plus, it can help you collect customer reviews. Some clients see improved G2 ratings with
          reviewers praising responsiveness to feedback.
        </p>
      </>
    ),
  },
  {
    question: 'Do you offer customer survey tools?',
    answer:
      "<p>We don't have a survey tool built into Canny. But, you can add your survey results and organize them on your feedback board.</p><p>You can also pass custom properties from your CRM into Canny if you want to know more about your users. For example, many customers will connect customers' NPS survey responses. This lets them see what features both happy and unhappy customers request.</p>",
    html: (
      <>
        <p>
          We don't have a survey tool built into Canny. But, you can add your survey results and
          organize them on your feedback board.
        </p>
        <p>
          You can also pass custom properties from your CRM into Canny if you want to know more
          about your users. For example, many customers will connect customers' NPS survey
          responses. This lets them see what features both happy and unhappy customers request.
        </p>
      </>
    ),
  },
  {
    question: 'What teams benefit most from using Canny?',
    answer:
      '<p>There are many teams that can benefit from Canny: </p><ul><li><p>Customer service can show customers you listen and act on their feedback</p></li><li><p>Product managers can see your most popular feedback. They also gain actionable insights by engaging directly with users</p></li><li><p>Sales can share feature requests from promising leads and keep them updated</p></li><li><p>Design gets feedback from website visitors about your user experience</p></li></ul><p>These are just a few examples. Canny benefits anyone who would like to understand and engage with your customers.</p>',
    html: (
      <>
        <p>There are many teams that can benefit from Canny: </p>
        <ul className="orderedList">
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Customer service can show customers you listen and act on their feedback
            </p>
          </li>
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Product managers can see your most popular feedback. They also gain actionable
              insights by engaging directly with users
            </p>
          </li>
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Sales can share feature requests from promising leads and keep them updated
            </p>
          </li>
          <li className="orderedListItem">
            <p className="orderedListItemCopy">
              Design gets feedback from website visitors about your user experience
            </p>
          </li>
        </ul>
        <p>
          These are just a few examples. Canny benefits anyone who would like to understand and
          engage with your customers.
        </p>
      </>
    ),
  },
  {
    question: 'Is Canny just a customer feedback tool?',
    answer:
      '<p>Our customer and client feedback tools are the core of our platform. But we offer a lot more than just feedback collection and management. Canny offers product management tools including <a href="https://canny.io/use-cases/public-roadmap" target="_blank" rel="noopener">roadmapping</a> and <a href="https://canny.io/features/product-changelog" target="_blank" rel="noopener">product announcement tools</a>. These tools are all connected to the product feedback you receive.</p>',
    html: (
      <p>
        Our customer and client feedback tools are the core of our platform. But we offer a lot more
        than just feedback collection and management. Canny offers product management tools
        including{' '}
        <a href="https://canny.io/use-cases/public-roadmap" target="_blank" rel="noopener">
          roadmapping
        </a>{' '}
        and{' '}
        <a href="https://canny.io/features/product-changelog" target="_blank" rel="noopener">
          product announcement tools
        </a>
        . These tools are all connected to the product feedback you receive.
      </p>
    ),
  },
];

const LandingCustomerFeedback = () => {
  const trackEvent = useContext(TrackEventContext);
  useEffect(() => {
    trackEvent('Viewed Customer Feedback Page');
  }, [trackEvent]);

  return (
    <main className="landingSolutionsPage">
      <LandingUseCaseHelmet {...meta} />
      <section className="fullWidthContainer">
        <div className="header">
          <div className="left">
            <div className="text">
              <h1>
                Customer feedback tools to help you build what users want and
                boost&nbsp;satisfaction
              </h1>
              <p className="subheading">
                Capture, organize, and prioritize all your feedback. Build the features that
                matter&nbsp;most.
              </p>
              <LandingCTAButtons renderDemoButton />
            </div>
          </div>
          <LazyLoadedImage
            loading="eager"
            src={LandingHero}
            alt="Comments and reviews from customers"
            className="noHide"
          />
        </div>
      </section>
      <div className="mainContentContainer">
        <LandingHowItWorks plain />
        {points.map((point, index) => {
          return <LandingPoint key={index} {...point} />;
        })}
        <Quotes
          title={<>Thousands of companies make managing feature requests easy with&nbsp;Canny</>}>
          {quotes.map((quote, index) => {
            return <Quote key={index} {...quote} />;
          })}
        </Quotes>
        <LandingFeaturesCTA />
        <LandingFooterCTA style="solid" expanded={true} />
        <FAQs faqs={faqs} />
      </div>
    </main>
  );
};

export default LandingCustomerFeedback;
